// =============================================================================
// setupTargets — one entry per Config setting a floor screen points at.
//
// A screen that cannot work until something is set up ("No lanes yet") used
// to say "set it up in Config → …" in plain text, to every reader. A loader
// has no Config: the words sent them to a step they cannot take, and a link
// would have bounced them to Home (RequireModule). <SetupPointer target/>
// reads this list and shows the place as a link to a reader who can open
// Config, and to anyone else who does it ("A manager sets up loading-bay
// lanes: ask your manager").
//
// Each entry says:
//   tab / section — where it lives (packhouseConfigHref; `section` is the
//                   heading on the Config page, so the pointer names it as
//                   the reader will see it)
//   place         — the words of the link ("Config → Storage → …")
//   who / does    — who sets it, in operator words: 'manager' for the
//                   settings any Config holder saves, 'admin' where the screen
//                   has always said an admin does it
//   minRole       — the section renders only for this role and above (its
//                   own `isManagerOrAbove` gate). Opening Config is not enough:
//                   a reader below it gets "ask your manager", never a link to
//                   a section that is not there for them. Must match the
//                   section's CONFIG_SETTINGS entry (the test pins it)
//
// Add an entry when a new screen points at a setting; never hand-write a
// "set it up in Config" sentence.
// =============================================================================

import type { PackhouseConfigTab } from '@/lib/packhouseConfigHref'
import { isManagerOrAbove } from '@/lib/roles'
import type { UserRole } from '@/types/database'

/** The lowest role a Config section renders for, beyond the /config gate.
 *  'manager' = the section's own `isManagerOrAbove(me.role)` check. */
export type SectionMinRole = 'manager'

/** Does `role` see a section gated at `minRole`? No gate → yes. */
export function meetsMinRole(minRole: SectionMinRole | undefined, role: UserRole | undefined | null): boolean {
  return minRole === undefined || isManagerOrAbove(role)
}

/** The note a role-gated section carries, in Find a setting and the pointer. */
export const MANAGERS_AND_ADMINS = 'managers and admins'

export interface SetupTarget {
  tab: PackhouseConfigTab
  section?: string
  place: string
  who: 'manager' | 'admin'
  does: string
  minRole?: SectionMinRole
}

export const SETUP_TARGETS = {
  loadingBayLanes: {
    tab: 'storage', section: 'Loading Bay Lanes', place: 'Config → Storage → Loading Bay Lanes',
    who: 'manager', does: 'sets up loading-bay lanes',
  },
  coldrooms: {
    tab: 'storage', section: 'Coldrooms', place: 'Config → Storage → Coldrooms',
    who: 'manager', does: 'sets up coldrooms and their layout grid',
  },
  packLines: {
    tab: 'operations', section: 'Pack lines', place: 'Config → Operations → Pack lines',
    who: 'manager', does: 'adds pack lines',
  },
  shrinkageRate: {
    tab: 'operations', section: 'Shrinkage valuation', place: 'Config → Operations → Shrinkage valuation',
    who: 'manager', does: 'sets the shrinkage R/kg rate',
  },
  emailGroups: {
    tab: 'admin', section: 'Email groups', place: 'Config → Admin → Email groups',
    who: 'manager', does: 'sets up grower groups', minRole: 'manager',
  },
} as const satisfies Record<string, SetupTarget>

export type SetupTargetKey = keyof typeof SETUP_TARGETS

/** "A manager sets up loading-bay lanes: ask your manager." With names (only
 *  ever passed for a reader who may read the member list): "… ask Thandi M or
 *  Pieter V." */
export function setupAskSentence(target: SetupTarget, names?: readonly string[] | null): string {
  const role = target.who === 'admin' ? 'An admin' : 'A manager'
  const ask = names && names.length > 0 ? `ask ${joinOr(names)}` : 'ask your manager'
  return `${role} ${target.does}: ${ask}.`
}

function joinOr(names: readonly string[]): string {
  if (names.length === 1) return names[0]
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`
}

// =============================================================================
// CONFIG_SETTINGS — every section on the Config page, and the few settings that
// live on a page of their own, for "Find a setting" (F19 UF-23).
//
// Config holds ~50 sections under seven tabs, and the cost is finding them: a
// manager looking for the print station has to know it is "Printing setup", a
// page of its own, and the season is a field inside "PPECB identity &
// defaults". Each entry carries:
//   title     — the section's heading exactly as the page shows it
//   tab       — the Config tab it sits on (absent for a page of its own)
//   words     — what people call it and its main field labels; matched too
//   only      — when the section shows at all ("force-cooling sites only")
//   minRole   — the section renders only for managers and admins: a reader
//               below that sees the result as text, "managers and admins
//               only", never a link to a section they cannot see
//   page      — a setting that lives off Config: its route, the module the
//               route gate asks for (a result the reader cannot open is never
//               offered — it would bounce them to Home), and where it sits
//
// SETUP_TARGETS above names sections from this same list (the test pins every
// pointer's tab + section to an entry here), so a pointer and the search can
// never name one section two ways. Rename a heading → rename it here.
// =============================================================================

export interface ConfigSetting {
  title: string
  tab?: PackhouseConfigTab
  words: readonly string[]
  only?: string
  minRole?: SectionMinRole
  page?: { to: string; module: string; where: string }
}

export const CONFIG_TAB_LABEL: Record<PackhouseConfigTab, string> = {
  business: 'Business', operations: 'Operations', dispatch: 'Dispatch', storage: 'Storage',
  commodities: 'Commodities', health: 'Setup', admin: 'Admin',
}

/** The Business-tab sections that need a PPECB flow. A business with no PPECB
 *  flow keeps the Business tab (letterhead, payment terms) but not these, so
 *  their Find results are hidden there (D2). Letterhead / branding and
 *  Payment terms are not listed: they stay findable. */
const PPECB_ONLY_TITLES: readonly string[] = [
  'PPECB environment', 'Inspection results', 'PPECB master data', 'PPECB API credentials',
  'eCert (TUM)', 'PPECB identity & defaults', 'PPECB agreements',
]

/** Drop the PPECB-only Business results when the business has no PPECB flow. */
export function visibleSettings(list: readonly ConfigSetting[], ppecbVisible: boolean): ConfigSetting[] {
  return list.filter(s => ppecbVisible || !(s.tab === 'business' && PPECB_ONLY_TITLES.includes(s.title)))
}

export const CONFIG_SETTINGS: readonly ConfigSetting[] = [
  // PPECB
  { tab: 'business', title: 'PPECB environment', words: ['test', 'live', 'production', 'sandbox'] },
  { tab: 'business', title: 'Inspection results', words: ['manual result', 'inspection'] },
  { tab: 'business', title: 'PPECB master data', words: ['sync', 'activity points', 'varieties', 'product packs', 'rejection reasons', 'service codes'] },
  { tab: 'business', title: 'PPECB API credentials', words: ['password', 'username', 'login', 'api key'] },
  { tab: 'business', title: 'eCert (TUM)', words: ['ecert', 'tum', 'client id', 'secret key', 'transmission site', 'system vendor id', 'business id', 'fbo codes', 'test connection', 'phytosanitary', 'trade unit'], only: MANAGERS_AND_ADMINS, minRole: 'manager' },
  { tab: 'business', title: 'PPECB identity & defaults', words: ['phc', 'fbo code', 'inspection point', 'exporter code', 'billing party', 'agreement code', 'target country', 'commodity code', 'pack code', 'orchard', 'booking ref', 'inspector', 'inventory code', 'season', 'c&f agent', 'lsp code', 'certificate issue office', 'production area', 'structure code'] },
  { tab: 'business', title: 'PPECB agreements', words: ['agreement'] },
  { tab: 'business', title: 'Payment terms', words: ['payment terms', 'days to pay', 'invoice terms', '30 days', 'due date', 'payment', 'terms', 'days', 'invoice due', 'credit'] },
  { tab: 'business', title: 'Letterhead / branding', words: ['logo', 'legal company name', 'business identity', 'tagline', 'contact email', 'phone', 'address', 'vat number', 'company registration', 'cipc', 'bank details', 'account number', 'branch code', 'swift'] },
  // Operations
  { tab: 'operations', title: 'Production stall alerts', words: ['stall threshold', 'whatsapp', 'manager number'] },
  { tab: 'operations', title: 'Shrinkage valuation', words: ['shrinkage rate', 'r/kg'] },
  { tab: 'operations', title: 'Labour defaults', words: ['lunch break', 'hourly rate', 'wage'] },
  { tab: 'operations', title: 'Clock-push hardening', words: ['allowed ips', 'scan age', 'clock'] },
  { tab: 'operations', title: 'Orchard estimation', words: ['carton weight', 'bin weight', 'estimates'] },
  { tab: 'operations', title: 'Dashboard processes', words: ['dashboard', 'home tiles'], only: MANAGERS_AND_ADMINS, minRole: 'manager' },
  { tab: 'operations', title: 'Packing products', words: ['packaging', 'products', 'cartons', 'punnets'] },
  { tab: 'operations', title: 'Piece-work rates', words: ['piece rate', 'piece work', 'rate', 'pay', 'wage', 'per punnet', 'per bin'] },
  { tab: 'operations', title: 'Pack lines', words: ['lines', 'pack schedule', 'packline'] },
  { tab: 'operations', title: 'Pack rates', words: ['rates', 'cartons per hour', 'throughput'] },
  { tab: 'operations', title: 'Pack shift', words: ['shift', 'start time', 'end time'] },
  { tab: 'operations', title: 'Line presets', words: ['presets', 'mark', 'target market', 'pack ref'] },
  { tab: 'operations', title: 'DUO packs', words: ['duo', 'combo pack'] },
  // Dispatch
  { tab: 'dispatch', title: 'Customers', words: ['destinations', 'depots', 'cold stores', 'dcs', 'buyers', 'contacts', 'accounts email'] },
  { tab: 'dispatch', title: 'Exporters', words: ['exporter', 'contacts'] },
  { tab: 'dispatch', title: 'Transporters', words: ['carriers', 'hauliers', 'trucks', 'biglogistics'] },
  { tab: 'dispatch', title: 'Fleet vehicles', words: ['vehicles', 'trucks', 'registration'] },
  { tab: 'dispatch', title: 'Target markets', words: ['markets', 'countries', 'regions'] },
  { tab: 'dispatch', title: 'Marks / brands', words: ['marks', 'brands', 'pack types'] },
  { tab: 'dispatch', title: 'Dispatch doc numbering', words: ['numbering', 'document numbers', 'delivery note numbers'] },
  { tab: 'dispatch', title: 'Paltrack PO file', words: ['paltrack', 'po file', 'sender code', 'depot account'] },
  { tab: 'dispatch', title: 'SSCC numbering', words: ['sscc', 'gs1 prefix', 'barcode numbers'] },
  // Storage
  { tab: 'storage', title: 'Coldrooms', words: ['cold rooms', 'cold storage', 'rooms', 'cells', 'cross-dock', 'grid'] },
  { tab: 'storage', title: 'Cooler allocation', words: ['coolers', 'cold'], only: 'force-cooling sites only' },
  { tab: 'storage', title: 'Cooling flow → rooms', words: ['cooling flow', 'cold', 'stages'], only: 'force-cooling sites only' },
  { tab: 'storage', title: 'Cooling SLAs & alerts', words: ['sla', 'cooling alerts', 'cold'], only: 'force-cooling sites only' },
  { tab: 'storage', title: 'Berry intake', words: ['berry', 'intake form', 'dolly', 'dolly weight', 'blueberries'], only: 'force-cooling sites only' },
  { tab: 'storage', title: 'Cooling defaults', words: ['precool', 'target temperature', 'crate weight', 'cold'], only: 'force-cooling sites only' },
  { tab: 'storage', title: 'Cold-storage rental', words: ['cold storage rate', 'storage charge', 'pallet per day', 'cold'] },
  { tab: 'storage', title: 'Loading Bay Lanes', words: ['lanes', 'loading bay', 'dispatch lanes'] },
  { tab: 'storage', title: 'Bin fleet', words: ['bins', 'bin rental', 'repair', 'replacement charge'] },
  { tab: 'storage', title: 'Bin types', words: ['bin tares', 'crates', 'crate tares', 'containers', 'tare', 'packhouse owns'] },
  { tab: 'storage', title: 'Received pallet weights', words: ['tares', 'pallet bases', 'carton weights', 'receipts'] },
  // Commodities
  { tab: 'commodities', title: 'Production lanes', words: ['lanes', 'flows', 'force cooling'] },
  { tab: 'commodities', title: 'Commodities we handle', words: ['fruit', 'commodities', 'citrus', 'blueberries'] },
  { tab: 'commodities', title: 'Linked runs (bulk sort)', words: ['linked', 'linked runs', 'bulk sort', 'sort'] },
  { tab: 'commodities', title: 'Delivery points', words: ['intake', 'weighbridge', 'gate'] },
  // Health
  { tab: 'health', title: 'Health', words: ['setup checks', 'setup doctor', 'findings', 'checks'] },
  { tab: 'health', title: 'Getting started', words: ['start', 'first steps', 'steps', 'new business', 'onboarding'] },
  // Admin
  { tab: 'admin', title: 'Money lock', words: ['pin', 'hide money', 'wages'] },
  { tab: 'admin', title: 'Snapshot backup', words: ['backup', 'export data'] },
  { tab: 'admin', title: 'Nightly backups', words: ['backup'] },
  { tab: 'admin', title: 'Email groups', words: ['recipients', 'contact groups', 'grower groups', 'bcc'], only: MANAGERS_AND_ADMINS, minRole: 'manager' },
  { tab: 'admin', title: 'Compliance reminders', words: ['reminders', 'expiry'] },
  { tab: 'admin', title: 'Underweight-pallet alerts', words: ['underweight', 'alert recipients'] },
  { tab: 'admin', title: 'Recycle bin', words: ['deleted', 'restore', 'undelete'] },
  { tab: 'admin', title: 'Client errors', words: ['errors', 'crashes'] },
  { tab: 'admin', title: 'Advisor performance', words: ['advisor'] },
  { tab: 'admin', title: 'Financial audit log', words: ['audit', 'history'] },
  { tab: 'admin', title: 'Privacy & Data', words: ['popia', 'privacy', 'personal data'] },
  { tab: 'admin', title: 'Industry benchmarks', words: ['data sharing', 'consent', 'benchmarks'] },
  // On a page of their own
  { title: 'Printing setup', words: ['printer', 'labels', 'print station', 'argox', 'bridge'],
    page: { to: '/printing-setup', module: 'pallets', where: 'Label printing — this station' } },
  { title: 'Label designer', words: ['label template', 'qr roll', 'labels'],
    page: { to: '/label-designer', module: 'pallets', where: 'Label designer' } },
  { title: 'Rate Card', words: ['rate cards', 'prices', 'pack prices', 'packing rates'],
    page: { to: '/financial?tab=rate_card', module: 'financial', where: 'Financial → Rate Card' } },
  { title: 'Transport rate cards', words: ['transport rates', 'rate cards', 'haulage rates'],
    page: { to: '/financial?tab=transport', module: 'financial', where: 'Financial → Transport' } },
  { title: 'Suppliers', words: ['supplier', 'vendors'],
    page: { to: '/suppliers', module: 'suppliers', where: 'Suppliers' } },
  { title: 'Reconciliation tolerance', words: ['tolerance', 'over tolerance'],
    page: { to: '/reconciliation', module: 'reconciliation', where: 'Reconciliation → Tolerance + range' } },
  { title: 'Pack & order codes', words: ['pack codes', 'order codes', 'pack ref codes', 'mark codes'],
    page: { to: '/pack-ref-codes', module: 'orders', where: 'Pack & order codes' } },
  { title: 'Users', words: ['invite', 'team', 'members', 'roles', 'logins'],
    page: { to: '/users', module: 'users', where: 'Users' } },
]

/** Lower-case, & → and, punctuation to spaces: "Cooling SLAs & alerts" →
 *  "cooling slas and alerts". */
function norm(s: string): string {
  return s.toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').trim()
}

/** A query word matches when it starts any word of the text, or — for a
 *  plural typed against a singular ("customers" vs "customer") — when its
 *  stem does. */
function wordHit(word: string, text: string): boolean {
  if (` ${text}`.includes(` ${word}`)) return true
  const stem = word.length > 3 && word.endsWith('s') ? word.slice(0, -1) : null
  return !!stem && ` ${text}`.includes(` ${stem}`)
}

/** The settings that answer `query`, best first: every query word must match
 *  the title, a word or the tab name. A title hit outranks a word hit, and a
 *  title that STARTS with the query outranks both. Empty query → nothing. */
export function findSettings(query: string, settings: readonly ConfigSetting[] = CONFIG_SETTINGS): ConfigSetting[] {
  const words = norm(query).split(' ').filter(Boolean)
  if (words.length === 0) return []
  const scored: { s: ConfigSetting; score: number; i: number }[] = []
  settings.forEach((s, i) => {
    const title = norm(s.title)
    const place = s.tab ? norm(CONFIG_TAB_LABEL[s.tab]) : norm(s.page?.where ?? '')
    const hay = `${title} ${s.words.map(norm).join(' ')} ${place}`
    if (!words.every(w => wordHit(w, hay))) return
    const q = words.join(' ')
    const score = title.startsWith(q) ? 3 : words.every(w => wordHit(w, title)) ? 2 : 1
    scored.push({ s, score, i })
  })
  return scored.sort((a, b) => b.score - a.score || a.i - b.i).map(x => x.s)
}
